import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Alert, Stepper, Step, StepLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, CircularProgress, Chip
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import api from '../../services/api';
import { contactPropertyService } from '../../services/contactPropertyService';

// These are the core fields from the Contact schema (server/models/contact.js)
const CONTACT_SCHEMA_FIELDS = [
  { key: 'contactId', label: 'Id (contactId)' },
  { key: 'email', label: 'Email' },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'gender', label: 'Gender' },
  { key: 'company', label: 'Company' },
  { key: 'city', label: 'City' },
  { key: 'phone_number', label: 'Phone Number' },
  { key: 'subscribed', label: 'Subscribed' },
];

/**
 * Simple CSV parser using FileReader.
 * Handles quoted fields and newlines inside quotes.
 */
function parseCSV(text) {
  const lines = text.split('\n').filter(l => l.trim() !== '');
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') { inQuotes = false; } else { current += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { result.push(current.trim()); current = ''; }
        else { current += ch; }
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map(line => {
    const values = parseLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = values[i] || ''; });
    return obj;
  });

  return { headers, rows };
}

const ImportContacts = () => {
  const { project } = useParams();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);

  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);

  const [customProperties, setCustomProperties] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    const loadProps = async () => {
      try {
        const props = await contactPropertyService.getContactProperties(project);
        // Filter out properties that already exist in the core schema to avoid duplicates
        const coreKeys = CONTACT_SCHEMA_FIELDS.map(f => f.key);
        setCustomProperties(props.filter(p => !coreKeys.includes(p.key)));
      } catch (err) {
        console.error('Failed to load custom properties');
      }
    };
    if (project) loadProps();
  }, [project]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParsing(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const { headers, rows } = parseCSV(evt.target.result);
        setCsvHeaders(headers);
        setCsvRows(rows);

        // Auto-map: try to guess matches by lowercased key
        const initialMap = {};
        headers.forEach(h => {
          const lc = h.toLowerCase().replace(/[\s_-]+/g, '_');
          const match = CONTACT_SCHEMA_FIELDS.find(f => f.key === lc || f.label.toLowerCase().replace(/[\s_-]+/g, '_') === lc);
          initialMap[h] = match ? match.key : '';
        });
        setMapping(initialMap);
        setActiveStep(1);
      } catch (err) {
        setError('Failed to parse CSV file. Please check the format.');
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setError('Failed to read the file.');
      setParsing(false);
    };
    reader.readAsText(selectedFile);
  };

  const handleMappingChange = (csvCol, propKey) => {
    setMapping(prev => ({ ...prev, [csvCol]: propKey }));
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);

    try {
      // Build contact objects from the CSV rows using the mapping
      const coreKeys = CONTACT_SCHEMA_FIELDS.map(f => f.key);
      const contacts = csvRows.map(row => {
        const contact = {};
        const customProps = {};

        Object.entries(mapping).forEach(([csvCol, targetKey]) => {
          if (!targetKey || !row[csvCol]) return;
          if (coreKeys.includes(targetKey)) {
            contact[targetKey] = row[csvCol];
          } else {
            // It's a custom property — store inside properties object
            customProps[targetKey] = row[csvCol];
          }
        });

        if (Object.keys(customProps).length > 0) {
          contact.properties = customProps;
        }
        return contact;
      });

      const response = await api.post('/contactbatchimport', {
        project,
        contacts
      });

      setImportResult(response.data);
      setActiveStep(2);
    } catch (err) {
      console.error('Import failed:', err);
      setError(err.response?.data?.error || 'Failed to import contacts.');
    } finally {
      setImporting(false);
    }
  };

  // All available mapping targets = core schema fields + custom properties
  const allMappingOptions = [
    ...CONTACT_SCHEMA_FIELDS.map(f => ({ key: f.key, label: f.label, group: 'Contact Fields' })),
    ...customProperties.map(p => ({ key: p.key, label: `${p.name} (${p.key})`, group: 'Custom Properties' })),
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Import Contacts
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 3 }}>
        <Step><StepLabel>Upload CSV</StepLabel></Step>
        <Step><StepLabel>Map Columns</StepLabel></Step>
        <Step><StepLabel>Complete</StepLabel></Step>
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {activeStep === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Upload a CSV file containing your contacts
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            The file must be in .csv format. You will map columns to contact fields in the next step.
          </Typography>

          <Button
            component="label"
            variant="contained"
            startIcon={parsing ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
            disabled={parsing}
            size="large"
          >
            {parsing ? 'Parsing...' : 'Select CSV File'}
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileChange}
            />
          </Button>
        </Paper>
      )}

      {activeStep === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Map CSV Columns to Contact Fields
            </Typography>
            <Chip label={`${csvRows.length} rows found`} color="info" variant="outlined" />
          </Box>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            For each column in your CSV, select the corresponding contact field. The <strong>"Id (contactId)"</strong> mapping is required.
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>CSV Column</strong></TableCell>
                  <TableCell><strong>Sample Value</strong></TableCell>
                  <TableCell><strong>Map To</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvHeaders.map((col) => (
                  <TableRow key={col}>
                    <TableCell>{col}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {csvRows[0]?.[col] || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Select
                        fullWidth
                        size="small"
                        displayEmpty
                        value={mapping[col] || ''}
                        onChange={(e) => handleMappingChange(col, e.target.value)}
                      >
                        <MenuItem value=""><em>-- Skip Column --</em></MenuItem>
                        {allMappingOptions.map(opt => (
                          <MenuItem key={opt.key} value={opt.key}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => { setActiveStep(0); setFile(null); setCsvHeaders([]); setCsvRows([]); }}>
              Back
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleImport}
              disabled={importing}
            >
              {importing ? <CircularProgress size={24} color="inherit" /> : `Import ${csvRows.length} Contacts`}
            </Button>
          </Box>
        </Paper>
      )}

      {activeStep === 2 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Alert severity="success" sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            {importResult?.imported
              ? `Successfully imported ${importResult.imported} contacts!`
              : 'Contacts imported successfully!'}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate(`/project/${project}/contact`)}
          >
            View Contacts
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default ImportContacts;
